/**
 * ActionButtons Component
 * Conditional action buttons based on order status
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-004
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle, Package, Calendar, Truck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import type { OrderStatus } from '@/types/order';

interface ActionButtonsProps {
  orderStatus: OrderStatus;
  onConfirm: (remarks?: string) => Promise<void>;
  onCancel: (reason: string) => Promise<void>;
  onMarkReady: () => Promise<void>;
  onScheduleDelivery: (deliveryDate: string) => Promise<void>;
  onViewDelivery?: () => void;
  onViewInvoice?: () => void;
}

export function ActionButtons({
  orderStatus,
  onConfirm,
  onCancel,
  onMarkReady,
  onScheduleDelivery,
  onViewDelivery,
  onViewInvoice,
}: ActionButtonsProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [confirmRemarks, setConfirmRemarks] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle confirm order
   */
  async function handleConfirm() {
    setIsProcessing(true);
    try {
      await onConfirm(confirmRemarks || undefined);
      setShowConfirmDialog(false);
      setConfirmRemarks('');
      toast.success('Order confirmed successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to confirm order');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle cancel order
   */
  async function handleCancel() {
    if (!cancelReason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }

    setIsProcessing(true);
    try {
      await onCancel(cancelReason);
      setShowCancelDialog(false);
      setCancelReason('');
      toast.success('Order cancelled successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel order');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle mark ready
   */
  async function handleMarkReady() {
    setIsProcessing(true);
    try {
      await onMarkReady();
      toast.success('Order marked as ready for dispatch');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to mark order as ready');
    } finally {
      setIsProcessing(false);
    }
  }

  /**
   * Handle schedule delivery
   */
  async function handleScheduleDelivery() {
    if (!deliveryDate) {
      toast.error('Please select a delivery date');
      return;
    }

    setIsProcessing(true);
    try {
      await onScheduleDelivery(deliveryDate);
      setShowScheduleDialog(false);
      setDeliveryDate('');
      toast.success('Delivery scheduled successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to schedule delivery');
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <>
      <div className="flex flex-wrap gap-3">
        {/* PENDING → CONFIRMED */}
        {orderStatus === 'PENDING' && (
          <>
            <Button
              onClick={() => setShowConfirmDialog(true)}
              disabled={isProcessing}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Confirm Order
            </Button>
            <Button
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
              variant="destructive"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Order
            </Button>
          </>
        )}

        {/* CONFIRMED → READY */}
        {orderStatus === 'CONFIRMED' && (
          <>
            <Button
              onClick={handleMarkReady}
              disabled={isProcessing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Package className="mr-2 h-4 w-4" />
              Ready for Dispatch
            </Button>
            <Button
              onClick={() => setShowCancelDialog(true)}
              disabled={isProcessing}
              variant="destructive"
            >
              <XCircle className="mr-2 h-4 w-4" />
              Cancel Order
            </Button>
          </>
        )}

        {/* READY → Schedule Delivery */}
        {orderStatus === 'READY' && (
          <Button
            onClick={() => setShowScheduleDialog(true)}
            disabled={isProcessing}
            className="bg-purple-600 hover:bg-purple-700"
          >
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Delivery
          </Button>
        )}

        {/* DISPATCHED → View Delivery */}
        {orderStatus === 'DISPATCHED' && onViewDelivery && (
          <Button onClick={onViewDelivery} variant="outline">
            <Truck className="mr-2 h-4 w-4" />
            View Delivery
          </Button>
        )}

        {/* DELIVERED → View Invoice */}
        {orderStatus === 'DELIVERED' && onViewInvoice && (
          <Button onClick={onViewInvoice} variant="outline">
            <FileText className="mr-2 h-4 w-4" />
            View Invoice
          </Button>
        )}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Order</DialogTitle>
            <DialogDescription>
              Are you sure you want to confirm this order? This action will notify the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="confirmRemarks">Remarks (Optional)</Label>
              <Textarea
                id="confirmRemarks"
                placeholder="Add any remarks..."
                value={confirmRemarks}
                onChange={(e) => setConfirmRemarks(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isProcessing}>
              Confirm Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Order</DialogTitle>
            <DialogDescription>
              Please provide a reason for cancelling this order. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cancelReason">Cancellation Reason *</Label>
              <Textarea
                id="cancelReason"
                placeholder="Reason for cancellation..."
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Go Back
            </Button>
            <Button
              onClick={handleCancel}
              disabled={isProcessing || !cancelReason.trim()}
              variant="destructive"
            >
              Cancel Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Delivery Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Schedule Delivery</DialogTitle>
            <DialogDescription>
              Select the delivery date for this order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="deliveryDate">Delivery Date *</Label>
              <Input
                id="deliveryDate"
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleScheduleDelivery}
              disabled={isProcessing || !deliveryDate}
            >
              Schedule Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
